import { useState } from 'react';

import { useGetPropertyByIdQuery } from '../../services/propertyApi';
import { useGetPhotosetsQuery } from '../../../property-assets-management/services/assetApi';
import { useGetRecentOrdersForPropertyQuery } from '../../../orders/services/orderApi';
import { getPermissionForAction } from '../../../../util/permissions';
import { getOrgSelectedAllData } from '../../../../services/org-data';
import { is4xxError } from '../../../../services/api-base';

import type { PropertyDetailMode } from '../types/property-detail.types';
import type { PropertyEntity } from '../../../../types/property.types';

interface UsePropertyDetailResult {
  property: PropertyEntity | undefined;
  isLoading: boolean;
  isNotFound: boolean;
  isError: boolean;
  refetch: () => void;
  photosetQuery: ReturnType<typeof useGetPhotosetsQuery>;
  ordersQuery: ReturnType<typeof useGetRecentOrdersForPropertyQuery>;
  mode: PropertyDetailMode;
  setMode: (next: PropertyDetailMode) => void;
  canEdit: boolean;
  isSyncManaged: boolean;
  currentOrgName: string;
}

export function usePropertyDetail(uuid: string): UsePropertyDetailResult {
  const [mode, setMode] = useState<PropertyDetailMode>('view');

  const propertyQuery = useGetPropertyByIdQuery(uuid, { skip: !uuid });
  // useGetPhotosetsQuery takes a plain string (the property UUID directly),
  // not an object — assetApi.getPhotosets is typed as builder.query<..., string>.
  const photosetQuery = useGetPhotosetsQuery(uuid, { skip: !uuid });
  const ordersQuery = useGetRecentOrdersForPropertyQuery(
    { propertyId: uuid, size: 3 },
    { skip: !uuid },
  );

  const property = propertyQuery.data;
  const isSyncManaged = property?.propertySource === 'Sync';
  const hasEditPerm = getPermissionForAction('Properties', 'PUT');
  const canEdit = !isSyncManaged && hasEditPerm;

  // Not-found detection covers two backend shapes:
  //   1. Query succeeded with empty content (Task 7 fix commit 7eacd7cf —
  //      getPropertyById returns undefined on empty content rather than
  //      throwing).
  //   2. Query errored with 4xx (invalid UUID like `/properties/list`, or
  //      404). Semantically equivalent to "no such property"; retry won't
  //      help, so route to the not-found hero instead of the error hero.
  const isClientNotFound = propertyQuery.isError && is4xxError(propertyQuery.error);
  const isNotFound =
    !propertyQuery.isLoading &&
    ((!propertyQuery.isError && !propertyQuery.data) || isClientNotFound);

  // Reserve isError for genuinely retryable failures (5xx, FETCH_ERROR,
  // PARSING_ERROR). 4xx is handled by isNotFound above.
  const isError = propertyQuery.isError && !isClientNotFound;

  // The selected org's display name, for the not-found copy. One OrgData shape
  // since DV-3012, so there is no longer a customer/admin field to probe for —
  // and this now resolves for internal admins, who previously always fell
  // through to the generic string (BC-9).
  const orgItem = getOrgSelectedAllData()[0];
  const currentOrgName = orgItem?.name ?? 'this organization';

  return {
    property,
    isLoading: propertyQuery.isLoading,
    isNotFound,
    isError,
    refetch: propertyQuery.refetch,
    photosetQuery,
    ordersQuery,
    mode,
    setMode,
    canEdit,
    isSyncManaged,
    currentOrgName,
  };
}
